import React from 'react'
import { useTranslation } from 'react-i18next'
import { User, Users } from 'lucide-react'
import useAppStore from '../../../store/useAppStore'
import { formatNumber } from '../../../utils/formatters'
import ProfileLink from '../../../components/ProfileLink'
import TeamTag from '../../../components/TeamTag'

const RANK_STYLES = {
  1: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  2: 'bg-slate-300/15 text-slate-200 border-slate-300/30',
  3: 'bg-amber-700/20 text-amber-500 border-amber-600/40'
}

/**
 * Лидерборд Rush Drop: top5 + своя строка снизу.
 * Своё место в top5 не помечено флагом — сравниваем chat_id со своим.
 * В режиме lottery_equal таблица информационная — подчёркиваем это подписью.
 */
const RushDropLeaderboard = ({ top5, you, participants, winnerMode }) => {
  const { t } = useTranslation()
  const myChatId = useAppStore((state) => state.user?.telegramid)
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)

  const rows = Array.isArray(top5) ? top5 : []
  const participantsCount = Number(participants || 0)
  const isLotteryEqual = winnerMode === 'lottery_equal'

  const renderAvatar = (entry) => {
    if (entry?.avatar) {
      return (
        <img
          src={entry.avatar}
          alt=""
          className="h-8 w-8 rounded-full border border-white/15 object-cover"
          loading="lazy"
          onError={(e) => {
            // Фолбэк при ошибке загрузки аватарки
            e.currentTarget.style.display = 'none'
          }}
        />
      )
    }
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
        <User size={14} className="text-text-secondary" />
      </div>
    )
  }

  const renderRow = (entry, isMine = false) => (
    <div
      key={`${entry.rank}-${entry.chat_id ?? 'anon'}`}
      className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
        isMine ? 'border-accent-primary/40 bg-accent-primary/10' : 'border-white/5 bg-white/[0.02]'
      }`}
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border text-xs font-bold tabular-nums ${
          RANK_STYLES[entry.rank] || 'border-white/10 bg-white/5 text-text-secondary'
        }`}
      >
        {entry.rank}
      </span>
      {/* Аватар кликабелен: slug === null → игрок не кликабелен */}
      <ProfileLink slug={entry.slug} className="shrink-0">
        {renderAvatar(entry)}
      </ProfileLink>
      <p className="min-w-0 flex-1 truncate text-sm font-medium text-white">
        <TeamTag team={entry?.team} className="" />
        <ProfileLink slug={entry.slug}>
          {entry?.name || t('rushdrop.leaderboard.player', 'Игрок')}
        </ProfileLink>
      </p>
      <span className="shrink-0 text-sm font-semibold tabular-nums text-text-secondary">
        {formatNumber(Number(entry?.points || 0))}
      </span>
    </div>
  )

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-bold text-white">
          <Users size={18} className="text-accent-primary" />
          {t('rushdrop.leaderboard.title', 'Лидерборд')}
        </h2>
        <span className="text-xs text-text-secondary">
          {t('rushdrop.leaderboard.participants', 'Участников: {{count}}', {
            count: formatNumber(participantsCount)
          })}
        </span>
      </div>

      {isLotteryEqual && (
        <p className="mb-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-xs text-text-secondary">
          {t('rushdrop.leaderboard.lotteryNote', 'Равный жребий: приз может уйти любому участнику, таблица — информационная')}
        </p>
      )}

      <div className={`space-y-2 ${isLotteryEqual ? 'opacity-80' : ''}`}>
        {rows.map((entry) =>
          renderRow(entry, myChatId != null && String(entry?.chat_id) === String(myChatId))
        )}
        {rows.length === 0 && (
          <p className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-3 text-center text-xs text-text-secondary">
            {t('rushdrop.leaderboard.empty', 'Пока никто не выполнил все задания')}
          </p>
        )}
      </div>

      {/* Своя строка */}
      <div className="mt-3 border-t border-white/10 pt-3">
        {isGuest ? (
          <button type="button" onClick={openLogin} className="w-full rounded-xl bg-accent-primary px-4 py-2.5 text-sm font-bold text-dark-primary">{t('guest.login')}</button>
        ) : you?.qualified ? (
          <div className="flex items-center justify-between rounded-xl border border-accent-primary/30 bg-accent-primary/5 px-3 py-2 text-sm">
            <span className="font-medium text-white">
              {t('rushdrop.leaderboard.you', 'Ты')}: {t('rushdrop.status.placeByPoints', 'Место по баллам')}{' '}
              <b className="tabular-nums">{you.rank}</b>
            </span>
            <span className="text-text-secondary">
              {t('rushdrop.status.chance', 'Шанс')}:{' '}
              <b className="tabular-nums text-accent-primary">
                {Number(you.chance_pct || 0).toLocaleString()}%
              </b>
            </span>
          </div>
        ) : (
          <p className="text-center text-xs text-text-secondary">
            {t('rushdrop.leaderboard.notQualified', 'Выполни все задания, чтобы попасть в таблицу')}
          </p>
        )}
      </div>
    </section>
  )
}

export default RushDropLeaderboard
